import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { RespostasEtapa2CandidatoSchema } from '../../models/candidato.schemas';
import { enviarDocumentosEtapa2 } from './enviarDocumentosEtapa2';
import {
  ARQUIVOS_ETAPA2_INICIAIS,
  ARQUIVOS_FILHO_INICIAIS,
  ArquivosEtapa2,
  ArquivosFilhoEtapa2,
  ETAPAS_CANDIDATO_ETAPA2,
  RESPOSTAS_ETAPA2_INICIAIS,
  RespostasEtapa2Estado,
  etapaEstaCompleta,
  prepararRespostasParaEnvio,
} from './candidatoFormExternoEtapa2Page.definitions';
import {
  EtapaDadosComplementares,
  EtapaContatoEndereco,
  EtapaTrabalho,
  EtapaBeneficiosFamilia,
  EtapaPessoal,
  EtapaCuriosidades,
  EtapaDocumentos,
  EtapaAutorizacaoEtapa2,
} from './components';
import type { ModoDocumentoIdentidade } from './components/EtapaDocumentos';

interface CandidatoExternoEtapa2Data {
  nome: string;
  email: string;
  /** Dados do RG já respondidos na Etapa 1 (formato antigo) — pré-preenchem a Etapa 2. */
  orgaoEmissorRg?: string | null;
  orgaoEmissorRgUf?: string | null;
  dataEmissaoRg?: string | null;
}

const variants = {
  enter: { x: 50, opacity: 0, scale: 0.95 },
  center: { x: 0, opacity: 1, scale: 1, zIndex: 1, transition: { duration: 0.4, ease: 'easeInOut' } },
  exit: { x: -50, opacity: 0, scale: 0.95, filter: 'blur(10px)', zIndex: 0, transition: { duration: 0.3, ease: 'easeIn' } },
} as const;

/**
 * Formulário público (sem login) da Etapa 2 — liberado só depois que o recrutador marca
 * `proposta_aceita` (ver GerarLinkEtapa2 na lista de candidatos). Mesmo padrão de wizard
 * do `CandidatoFormExternoPage` (Etapa 1), mas com bem mais campos e upload de documentos
 * (ver submitCandidatoExternoEtapa2).
 */
export default function CandidatoFormExternoEtapa2Page() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  // Erro de carregamento (token inválido/expirado) trava a tela inteira, sem alternativa
  // mesmo. Erro de envio é diferente — tem que deixar a pessoa voltar e corrigir, por
  // isso é um estado separado, mostrado dentro do próprio wizard (ver render abaixo).
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [dados, setDados] = useState<CandidatoExternoEtapa2Data | null>(null);
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [respostas, setRespostas] = useState<RespostasEtapa2Estado>(RESPOSTAS_ETAPA2_INICIAIS);
  const [arquivos, setArquivos] = useState<ArquivosEtapa2>(ARQUIVOS_ETAPA2_INICIAIS);
  const [arquivosFilhos, setArquivosFilhos] = useState<ArquivosFilhoEtapa2[]>([]);
  const [modoIdentidade, setModoIdentidade] = useState<ModoDocumentoIdentidade>(null);
  const [enviando, setEnviando] = useState(false);
  /** "Enviando documentos (2 de 5)..." — cada anexo sobe separado, pode levar um tempo. */
  const [progressoEnvio, setProgressoEnvio] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    const functions = getFunctions(app, 'us-central1');
    const getCandidatoExternoEtapa2 = httpsCallable<{ token: string }, CandidatoExternoEtapa2Data>(
      functions,
      'getCandidatoExternoEtapa2'
    );
    getCandidatoExternoEtapa2({ token })
      .then((result) => {
        setDados(result.data);
        // Pré-preenche com o e-mail que o recrutador já cadastrou — evita o candidato
        // digitar um e-mail diferente por engano e quebrar o vínculo automático com a
        // Solicitação de Admissão (ver onSolicitacaoWrite). Editável pelo lápis.
        setRespostas((atual) => ({
          ...atual,
          emailPessoal: result.data.email ?? '',
          orgaoEmissorRg: result.data.orgaoEmissorRg ?? '',
          orgaoEmissorRgUf: result.data.orgaoEmissorRgUf ?? '',
          dataEmissaoRg: result.data.dataEmissaoRg ?? '',
        }));
      })
      .catch((err: any) => setErroCarregamento(err.message || 'Erro ao carregar o formulário.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    document.getElementById('wizard-content-stage')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [etapaAtual]);

  // Mantém arquivosFilhos do mesmo tamanho que respostas.filhos (cresce/encolhe quando o
  // candidato adiciona/remove um filho na etapa de Benefícios/Família).
  useEffect(() => {
    setArquivosFilhos((atual) => {
      if (atual.length === respostas.filhos.length) return atual;
      const novo = [...atual];
      while (novo.length < respostas.filhos.length) novo.push({ ...ARQUIVOS_FILHO_INICIAIS });
      novo.length = respostas.filhos.length;
      return novo;
    });
  }, [respostas.filhos.length]);

  const handleChange = <K extends keyof RespostasEtapa2Estado>(campo: K, valor: RespostasEtapa2Estado[K]) => {
    setRespostas((atual) => ({ ...atual, [campo]: valor }));
  };

  const handleChangeArquivo = (campo: keyof ArquivosEtapa2, arquivo: File | null) => {
    setArquivos((atual) => ({ ...atual, [campo]: arquivo }));
  };

  const handleChangeModoIdentidade = (modo: ModoDocumentoIdentidade) => {
    setModoIdentidade(modo);
    // Se saiu do modo "separado", o verso enviado antes não se aplica mais.
    if (modo !== 'verso') setArquivos((atual) => ({ ...atual, identidadeVerso: null }));
  };

  const handleChangeArquivoFilho = (index: number, campo: keyof ArquivosFilhoEtapa2, arquivo: File | null) => {
    setArquivosFilhos((atual) => {
      const novo = [...atual];
      novo[index] = { ...novo[index], [campo]: arquivo };
      return novo;
    });
  };

  const handleEnviar = async () => {
    if (!token) return;

    const parsed = RespostasEtapa2CandidatoSchema.safeParse({
      ...prepararRespostasParaEnvio(respostas),
      documentos: {
        identidadeFrenteUrl: null,
        identidadeVersoUrl: null,
        comprovanteEnderecoUrl: null,
        fotoRecenteUrl: null,
        certidaoCasamentoUrl: null,
        rgConjugeUrl: null,
      },
    });
    if (!parsed.success) {
      setErroEnvio(parsed.error.issues[0]?.message ?? 'Preencha todos os campos obrigatórios.');
      return;
    }

    setEnviando(true);
    setErroEnvio(null);
    try {
      // Um documento por vez (limite de tamanho vale por arquivo, erro diz qual falhou).
      const { documentosUrls, documentosFilhosUrls } = await enviarDocumentosEtapa2(
        token,
        arquivos,
        arquivosFilhos.slice(0, parsed.data.filhos.length),
        (enviados, total) => setProgressoEnvio(`Enviando documentos (${enviados + 1} de ${total})...`)
      );
      setProgressoEnvio('Enviando respostas...');

      const functions = getFunctions(app, 'us-central1');
      const submitCandidatoExternoEtapa2 = httpsCallable(functions, 'submitCandidatoExternoEtapa2');
      await submitCandidatoExternoEtapa2({ token, respostas: parsed.data, documentosUrls, documentosFilhosUrls });
      setEnviado(true);
    } catch (err: any) {
      setErroEnvio(err.message || 'Erro ao enviar o formulário.');
    } finally {
      setEnviando(false);
      setProgressoEnvio(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (erroCarregamento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border border-red-100 bg-red-50 p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-bold text-red-800">Não foi possível continuar</h2>
          <p className="text-red-600">{erroCarregamento}</p>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-2xl rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-lg md:p-12">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Cadastro enviado com sucesso!</h2>
          <p className="text-gray-600">Obrigado por completar seu cadastro de admissão. Em breve o RH entra em contato.</p>
        </div>
      </div>
    );
  }

  const etapas = ETAPAS_CANDIDATO_ETAPA2;
  const etapa = etapas[etapaAtual];
  const ultimaEtapa = etapaAtual === etapas.length - 1;
  const progresso = ((etapaAtual + 1) / etapas.length) * 100;
  const podeAvancar = etapaEstaCompleta(etapa, respostas);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans text-gray-900">
      <div className="bg-indigo-900 p-6 text-center text-white shadow-md">
        <h1 className="text-2xl font-bold">Cadastro de Admissão</h1>
        <p className="opacity-80">Parabéns, {dados?.nome}!</p>
      </div>

      <div id="wizard-content-stage" className="relative mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 px-2">
          <div className="mb-2 flex items-end justify-between">
            <span className="text-sm font-semibold text-indigo-900">
              Etapa {etapaAtual + 1} de {etapas.length}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-indigo-600 transition-all duration-500 ease-out"
              style={{ width: `${progresso}%` }}
            />
          </div>
        </div>

        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={etapaAtual}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg"
          >
            <div className="p-8 md:p-10">
              {etapa === 'introducao' && (
                <>
                  <h2 className="mb-4 text-2xl font-bold text-gray-900">Vamos finalizar seu cadastro!</h2>
                  <p className="text-gray-600">
                    Sua proposta foi aceita — agora precisamos de mais algumas informações pra dar sequência à sua
                    admissão. Leva alguns minutos.
                  </p>
                </>
              )}
              {etapa === 'dados_complementares' && (
                <EtapaDadosComplementares respostas={respostas} onChange={handleChange} />
              )}
              {etapa === 'contato_endereco' && <EtapaContatoEndereco respostas={respostas} onChange={handleChange} />}
              {etapa === 'trabalho' && <EtapaTrabalho respostas={respostas} onChange={handleChange} />}
              {etapa === 'beneficios_familia' && (
                <EtapaBeneficiosFamilia respostas={respostas} onChange={handleChange} />
              )}
              {etapa === 'pessoal' && <EtapaPessoal respostas={respostas} onChange={handleChange} />}
              {etapa === 'curiosidades' && <EtapaCuriosidades respostas={respostas} onChange={handleChange} />}
              {etapa === 'documentos' && (
                <EtapaDocumentos
                  estadoCivil={respostas.estadoCivil}
                  possuiDeficiencia={respostas.possuiDeficiencia}
                  filhos={respostas.filhos}
                  arquivos={arquivos}
                  arquivosFilhos={arquivosFilhos}
                  modoIdentidade={modoIdentidade}
                  onChangeModoIdentidade={handleChangeModoIdentidade}
                  onChangeArquivo={handleChangeArquivo}
                  onChangeArquivoFilho={handleChangeArquivoFilho}
                />
              )}
              {etapa === 'autorizacao' && (
                <EtapaAutorizacaoEtapa2
                  autorizacaoTermo={respostas.autorizacaoTermo}
                  onChange={(valor) => handleChange('autorizacaoTermo', valor)}
                />
              )}
              {erroEnvio && <p className="mt-4 text-sm text-red-600">{erroEnvio}</p>}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between px-2">
          <button
            onClick={() => setEtapaAtual((atual) => Math.max(0, atual - 1))}
            disabled={etapaAtual === 0}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              etapaAtual === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-white hover:text-indigo-600'
            }`}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Anterior
          </button>

          <button
            onClick={() => (ultimaEtapa ? handleEnviar() : setEtapaAtual((atual) => atual + 1))}
            disabled={!podeAvancar || enviando}
            className={`flex items-center gap-2 rounded-full px-8 py-3 font-bold shadow-lg shadow-indigo-200 transition-all ${
              !podeAvancar || enviando
                ? 'cursor-not-allowed bg-gray-200 text-gray-400 shadow-none'
                : 'bg-indigo-600 text-white hover:scale-105 hover:bg-indigo-700 active:scale-95'
            }`}
          >
            {enviando ? progressoEnvio ?? 'Enviando...' : etapa === 'introducao' ? 'Começar' : ultimaEtapa ? 'Finalizar' : 'Próximo'}
          </button>
        </div>
      </div>
    </div>
  );
}
